import { useEffect, useState } from 'react'
import { SparkyAnim, type SparkyAnimState } from '@/components/SparkyAnim'
import { cn } from '@/lib/utils'

/** Rotating Dutch reassurance copy shown while an AI tool is working. */
const CAPTIONS = [
  'Sparky leest de documenten…',
  'Even de cijfers nalopen…',
  'Bezig met analyseren…',
  'Nog heel even geduld…',
] as const

const CAPTION_MS = 2500

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export interface SparkyThinkingProps {
  /** Optional override for the caption line (e.g. a tool-specific step label). */
  caption?: string
  /** Sparky animation state; defaults to 'thinking'. */
  state?: SparkyAnimState
  /** `inset` drops the card's shadow and padding, for use inside a panel (no card in a card, U-167). */
  variant?: 'card' | 'inset'
  className?: string
}

/**
 * Animated Sparky mascot for AI working states: shows the SparkyAnim
 * mascot in the given state and rotates reassuring Dutch captions.
 * `prefers-reduced-motion` freezes the animation (CSS) and the captions.
 */
export function SparkyThinking({ caption, state = 'thinking', variant = 'card', className }: SparkyThinkingProps) {
  const reduceMotion = prefersReducedMotion()
  const [captionIndex, setCaptionIndex] = useState(0)

  useEffect(() => {
    if (reduceMotion || caption) return
    const captionTimer = window.setInterval(() => {
      setCaptionIndex((current) => (current + 1) % CAPTIONS.length)
    }, CAPTION_MS)
    return () => window.clearInterval(captionTimer)
  }, [reduceMotion, caption])

  const text = caption ?? CAPTIONS[captionIndex]

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-2 text-center',
        variant === 'card' ? 'vonk-card' : 'py-4',
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <SparkyAnim state={state} size={140} />
      <p className="font-heading text-base text-primary">{text}</p>
    </div>
  )
}
